/**
 * Helper function to open the user profile drawer
 * This leverages the document.x.a pattern used by SuccessionDrawerWithListener
 * 
 * The document.x pattern is initialized in SuccessionDrawerWithListener and 
 * allows communication between components without Redux dependencies
 * 
 * @param {string} userName - The username to display in the drawer
 * @param {Object} options - Optional configuration
 * @param {boolean} options.fromDeal - Whether the drawer is opened from a deal context
 * @returns {boolean} - Whether the operation was successful
 */
export const openProfileDrawer = (userName, options = {}) => {
    if (!userName) {
        console.error("Cannot open profile drawer: No username provided");
        return false;
    }

    try {
        // If opened from a deal, set a flag to hide the rolodex button
        if (options.fromDeal) {
            sessionStorage.setItem('profileDrawer_fromDeal', 'true');
            if (options.dealInterest && typeof options.dealInterest === 'object') {
                const dealInterestPayload = {
                    id: options.dealInterest.id,
                    workflow_state: options.dealInterest.workflow_state,
                    deal: options.dealInterest.deal
                        ? { id: options.dealInterest.deal.id, deal_name: options.dealInterest.deal.deal_name }
                        : undefined,
                };
                sessionStorage.setItem('profileDrawer_dealInterest', JSON.stringify(dealInterestPayload));
            } else {
                sessionStorage.removeItem('profileDrawer_dealInterest');
            }
        } else {
            // Clear the flag if it exists
            sessionStorage.removeItem('profileDrawer_fromDeal');
            sessionStorage.removeItem('profileDrawer_dealInterest');
        }
        
        // Check if document.x is initialized
        if (document.x) {
            document.x.a = userName;
            console.log("Set document.x.a to", userName);
            return true;
        } else {
            console.error("document.x not initialized - check if SuccessionDrawerWithListener is mounted");
            return false;
        }
    } catch (error) {
        console.error("Error opening profile drawer:", error);
        return false;
    }
};

/**
 * Opens the profile drawer and shows the Deal tab with proposal information
 * 
 * This function will:
 * 1. Open the profile drawer for the given user
 * 2. Set a session storage flag to indicate the Deal tab should be active
 * 3. Set a session storage flag to indicate proposal should be focused
 * 
 * The SuccessionProfileDrawer component will check for these flags on mount
 * 
 * @param {string} userName - The username to display in the drawer
 * @returns {boolean} - Whether the operation was successful
 */
export const openProfileDrawerToDealProposal = (userName, dealInterest = null) => {
    if (!userName) {
        console.error("Cannot open profile drawer: No username provided");
        return false;
    }

    try {
        // Store our intention in sessionStorage
        sessionStorage.setItem('profileDrawer_tab', '5'); // 5 is the Deal tab
        sessionStorage.setItem('profileDrawer_focusOn', 'proposal');

        // Then open the drawer, marking it as from deal
        return openProfileDrawer(userName, { fromDeal: true, dealInterest });
    } catch (error) {
        console.error("Error opening profile drawer to deal tab:", error);
        return false;
    }
};